import { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  deleteShare,
  listShares,
  shareAbsoluteUrl,
  type ShareRow,
} from '@/lib/zcall';

interface Props {
  userId: number;
  /** Change de valeur pour forcer le rechargement (après création d'un lien). */
  refreshKey?: number;
}

function formatDate(value?: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Liste des liens de partage créés depuis Documents, avec leur compteur de
 * consultations / téléchargements et un QR code prêt à scanner.
 */
export default function ShareLinksPanel({ userId, refreshKey = 0 }: Props) {
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openQr, setOpenQr] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await listShares(userId);
    setShares(rows);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const copy = async (row: ShareRow) => {
    try {
      await navigator.clipboard.writeText(shareAbsoluteUrl(row.token));
      setCopied(row.id);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      setCopied(null);
    }
  };

  const remove = async (id: number) => {
    await deleteShare(id);
    setShares((prev) => prev.filter((s) => s.id !== id));
    setConfirmId(null);
    if (openQr === id) setOpenQr(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-lg px-4 py-6 text-center">
        <i className="ri-share-line text-2xl text-foreground-300"></i>
        <p className="text-sm text-foreground-600 mt-2">Aucun lien de partage pour l&apos;instant</p>
        <p className="text-xs text-foreground-400 mt-1">
          Utilisez l&apos;icône Partager sur un dossier ou un fichier pour créer un lien public.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {shares.map((row) => {
        const isFolder = (row.kind || 'docs') === 'folder';
        const count = (row.content || []).length;
        return (
          <div key={row.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-3.5 py-3">
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isFolder ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`}>
                <i className={isFolder ? 'ri-folder-3-fill' : 'ri-file-text-line'}></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-900 truncate">{row.title || 'Documents partagés'}</p>
                <div className="flex items-center gap-3 text-[11px] text-foreground-400 flex-wrap">
                  <span>{count} fichier{count !== 1 ? 's' : ''}</span>
                  {row.created_at && <span>{formatDate(row.created_at)}</span>}
                  {row.password_hash && (
                    <span className="flex items-center gap-0.5"><i className="ri-lock-line"></i>protégé</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="flex items-center gap-1 text-xs text-foreground-600" title="Consultations">
                  <i className="ri-eye-line text-secondary-600"></i>
                  {Number(row.view_count) || 0}
                </span>
                <span className="flex items-center gap-1 text-xs text-foreground-600" title="Téléchargements">
                  <i className="ri-download-2-line text-accent-600"></i>
                  {Number(row.download_count) || 0}
                </span>
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  onClick={() => copy(row)}
                  title="Copier le lien"
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 cursor-pointer transition-colors"
                >
                  <i className={copied === row.id ? 'ri-check-line text-sm text-emerald-600' : 'ri-file-copy-line text-sm'}></i>
                </button>
                <button
                  onClick={() => setOpenQr((prev) => (prev === row.id ? null : row.id))}
                  title="QR code"
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-secondary-700 hover:bg-secondary-50 cursor-pointer transition-colors"
                >
                  <i className="ri-qr-code-line text-sm"></i>
                </button>
                {confirmId === row.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => remove(row.id)}
                      className="px-2 py-1 rounded bg-red-500 text-white text-[11px] font-medium cursor-pointer hover:bg-red-600 whitespace-nowrap"
                    >
                      Confirmer
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-2 py-1 rounded bg-background-100 text-foreground-600 text-[11px] cursor-pointer hover:bg-background-200/70 whitespace-nowrap"
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(row.id)}
                    title="Supprimer le lien"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>

            {openQr === row.id && (
              <div className="flex flex-col items-center gap-2 px-3.5 pb-4 pt-1 bg-background-100/60">
                <div className="p-3 bg-white rounded-xl border border-background-200/70">
                  <QRCodeSVG value={shareAbsoluteUrl(row.token)} size={150} level="H" fgColor="#172C43" bgColor="#ffffff" />
                </div>
                <p className="text-[11px] text-foreground-500 break-all text-center max-w-full px-2">
                  {shareAbsoluteUrl(row.token)}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}